import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, Truck, CalendarClock, Bell, Layers3, UserCircle2, KeyRound, LogOut, ShieldCheck, Building2, FolderOpen, Wallet, Fuel, GitMerge, Users, Gavel, FlaskConical, Sparkles, Wrench, LifeBuoy } from "lucide-react";
import { cn } from "@/lib/utils";
import { getActingTenant, setActingTenant, getPendingReviewCount } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { can, isDriver, isConsoleMode, isAssistMode, moduleOn, ROLE_LABELS } from "@/lib/rbac";
import { driverErrorCode, useMyProfile } from "@/components/me/DriverShell";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/vehicules", label: "Véhicules", icon: Truck, testId: "nav-vehicles", module: "vehicles" },
  { to: "/documents", label: "Documents", icon: FolderOpen, testId: "nav-documents", module: "documents" },
  { to: "/timeline", label: "Échéances", icon: CalendarClock, testId: "nav-timeline", module: "deadlines" },
  { to: "/couts", label: "Coûts", icon: Wallet, testId: "nav-costs", module: "costs" },
  { to: "/energie", label: "Énergie", icon: Fuel, testId: "nav-energy", module: "energy" },
  { to: "/conducteurs", label: "Conducteurs", icon: Users, testId: "nav-drivers", module: "drivers" },
  { to: "/amendes", label: "Amendes", icon: Gavel, testId: "nav-fines", module: "fines" },
  { to: "/assistant", label: "Assistant IA", icon: Sparkles, testId: "nav-ai", cap: "ai.use", module: "ai" },
  { to: "/alertes", label: "Alertes", icon: Bell, testId: "nav-alerts", module: "alerts" },
  { to: "/integrite", label: "Intégrité", icon: ShieldCheck, testId: "nav-integrity", cap: "pages.integrity", module: "integrity" },
  { to: "/admin/correspondances", label: "Correspondances", icon: GitMerge, testId: "nav-legacy", cap: "pages.legacy" },
];

// Console Super Admin (hors mode assistance) : aucune navigation métier, uniquement l'administration plateforme.
const SUPERADMIN_NAV = [
  { to: "/admin", label: "Administration des clients", icon: Building2, testId: "nav-admin" },
  { to: "/admin/outils", label: "Outils techniques", icon: Wrench, testId: "nav-admin-tools" },
];

// Lot H — navigation chauffeur : exactement 3 entrées (+ déconnexion dans le menu compte), même Layout, même charte.
const DRIVER_NAV = [
  { to: "/mes-pleins", label: "Mes pleins", icon: Fuel, testId: "nav-me-fuel", module: "energy" },
  { to: "/mes-amendes", label: "Mes amendes", icon: Gavel, testId: "nav-me-fines", module: "fines" },
  { to: "/mes-vehicules", label: "Mes véhicules", icon: Truck, testId: "nav-me-vehicles" },
];

function useNavItems(user) {
  const driver = isDriver(user);
  const profile = useMyProfile({ enabled: driver });
  if (driver) return profile.isError && driverErrorCode(profile.error) ? [] : DRIVER_NAV.filter((i) => moduleOn(user, i.module));
  if (isConsoleMode(user)) return SUPERADMIN_NAV;
  return NAV.filter((i) => (!i.cap || can(user, i.cap)) && moduleOn(user, i.module));
}

function Brand() {
  return (
    <Link to="/" className="flex shrink-0 items-center gap-2.5" data-testid="brand-logo">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white">
        <Layers3 className="h-5 w-5" />
      </div>
      <div className="leading-tight">
        <p className="font-display text-lg font-extrabold tracking-tight text-slate-900">LogiTrak</p>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Fleet Admin</p>
      </div>
    </Link>
  );
}

function TopTabs() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canValidate = can(user, "documents.write") && moduleOn(user, "documents") && !isConsoleMode(user);
  const { data: pending } = useQuery({
    queryKey: ["documents", "pending-review-count"],
    queryFn: getPendingReviewCount,
    enabled: canValidate,
    refetchInterval: 60000,
  });
  const pendingCount = canValidate ? pending?.count || 0 : 0;
  const items = useNavItems(user);
  return (
    <nav className="no-scrollbar -mb-px flex items-center gap-1 overflow-x-auto" data-testid="top-nav" data-role={user?.role || ""}>
      {items.map(({ to, label, icon: Icon, testId }) => {
        const active = pathname === to || (to !== "/" && pathname.startsWith(`${to}/`));
        return (
          <Link
            key={to}
            to={to}
            data-testid={testId}
            className={cn(
              "group flex shrink-0 items-center gap-2 border-b-2 px-3.5 py-3 text-sm font-medium transition-colors duration-200",
              active
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-900"
            )}
          >
            <Icon
              className={cn(
                "h-[18px] w-[18px] shrink-0 transition-colors",
                active ? "text-slate-900" : "text-slate-400 group-hover:text-slate-600"
              )}
            />
            {label}
            {to === "/documents" && pendingCount > 0 && (
              <span
                data-testid="nav-documents-pending-badge"
                title={`${pendingCount} scan${pendingCount > 1 ? "s" : ""} analysé${pendingCount > 1 ? "s" : ""} en attente de validation — cliquer pour les afficher`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/documents?a_valider=1"); }}
                className="flex h-5 min-w-[20px] cursor-pointer items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white transition-colors hover:bg-amber-600"
              >
                {pendingCount > 99 ? "99+" : pendingCount}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [pwdOpen, setPwdOpen] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="user-menu-btn"
            aria-label="Compte"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            <UserCircle2 className="h-6 w-6" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel data-testid="user-menu-email" className="truncate text-xs font-normal text-slate-500">
            {user?.email}
            {user?.role && !["admin", "superadmin"].includes(user.role) && (
              <span className="ml-1 font-semibold text-amber-600" data-testid="user-menu-role">· {ROLE_LABELS[user.role] || user.role}</span>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {!user?.impersonation && (
            <DropdownMenuItem data-testid="user-menu-change-password" onSelect={() => setPwdOpen(true)}>
              <KeyRound className="mr-2 h-4 w-4 text-slate-400" /> Changer le mot de passe
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid="user-menu-logout" onSelect={logout} className="text-red-600 focus:text-red-600">
            <LogOut className="mr-2 h-4 w-4" /> {user?.impersonation ? "Quitter le compte" : "Se déconnecter"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </>
  );
}

function DemoBanner() {
  const { user } = useAuth();
  if (!user?.tenant_demo) return null;
  return (
    <div data-testid="demo-mode-banner"
      className="flex items-center justify-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-center text-xs font-medium text-amber-800 sm:px-6 lg:px-8">
      <FlaskConical className="h-3.5 w-3.5 shrink-0 text-amber-600" />
      <span>Données de démonstration{user?.tenant_name ? ` · ${user.tenant_name}` : ""} — environnement de présentation</span>
    </div>
  );
}

function ImpersonationBanner() {
  const { user, exitImpersonation } = useAuth();
  const [leaving, setLeaving] = useState(false);
  if (!user?.impersonation) return null;
  const roleLabel = ROLE_LABELS[user.role] || user.role;
  return (
    <div data-testid="impersonation-banner"
      className="flex flex-wrap items-center justify-between gap-2 bg-indigo-700 px-4 py-2 text-sm text-white sm:px-6 lg:px-8">
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1 font-medium">
        <span className="flex items-center gap-2"><LifeBuoy className="h-4 w-4" /> Mode assistance</span>
        <span className="text-indigo-100">Connecté en tant que : <strong className="text-white" data-testid="impersonation-user">{user.email}</strong></span>
        <span className="text-indigo-100">Rôle : <strong className="text-white capitalize" data-testid="impersonation-role">{roleLabel}</strong></span>
        {user.tenant_name && <span className="text-indigo-200">· {user.tenant_name}</span>}
      </span>
      <button data-testid="impersonation-exit-btn" disabled={leaving}
        onClick={() => { setLeaving(true); exitImpersonation(); }}
        className="flex items-center gap-1.5 rounded-md bg-white/15 px-3 py-1 text-xs font-semibold transition-colors hover:bg-white/25 disabled:opacity-60">
        <LogOut className="h-3.5 w-3.5" /> Quitter le compte
      </button>
    </div>
  );
}

function ActingTenantBanner() {
  const { user } = useAuth();
  const acting = getActingTenant();
  if (!isAssistMode(user)) return null;
  const name = user?.acting_tenant?.name || acting.name || acting.id;
  const mods = user?.tenant_modules ? Object.values(user.tenant_modules).filter(Boolean).length : null;
  return (
    <div data-testid="acting-tenant-banner"
      className="flex flex-wrap items-center justify-between gap-2 bg-indigo-700 px-4 py-2 text-sm text-white sm:px-6 lg:px-8">
      <span className="flex flex-wrap items-center gap-2 font-medium">
        <LifeBuoy className="h-4 w-4" />
        <span data-testid="acting-tenant-label">Mode assistance · {name}</span>
        <span className="text-xs font-normal text-indigo-100">
          — droits administrateur du client{mods != null ? ` · ${mods}/10 modules actifs` : ""}
        </span>
      </span>
      <button data-testid="acting-tenant-exit-btn"
        onClick={() => { setActingTenant(null); window.location.assign("/admin"); }}
        className="flex items-center gap-1.5 rounded-md bg-white/15 px-3 py-1 text-xs font-semibold transition-colors hover:bg-white/25">
        <LogOut className="h-3.5 w-3.5" /> Quitter le compte
      </button>
    </div>
  );
}

function moduleLabel(user) {
  if (isDriver(user)) return "Espace chauffeur";
  if (isConsoleMode(user)) return "Console Super Admin";
  if (isAssistMode(user) || user?.impersonation) return "Mode assistance";
  return "Gestion administrative de flotte";
}

export default function Layout({ children }) {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur-md">
        <DemoBanner />
        <ImpersonationBanner />
        <ActingTenantBanner />
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Brand />
          <div className="flex items-center gap-4">
            <div className="hidden flex-col text-right sm:flex">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Module</p>
              <p className="font-display text-sm font-bold leading-none tracking-tight text-slate-900" data-testid="layout-module-label">
                {moduleLabel(user)}
              </p>
            </div>
            <UserMenu />
          </div>
        </div>
        <div className="border-t border-slate-100">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
            <TopTabs />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8">{children}</main>
    </div>
  );
}
